import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, FaCheckCircle, FaEnvelope, FaPhone, FaBoxOpen, 
  FaTrashAlt, FaTrash // Icon update
} from 'react-icons/fa';

// ... Types interface same as before ...
interface Product { id: number; productName: string; price: number; description: string; status: string; moq: string; unit: string; category: { id: number; name: string; }; images: { imageUrl: string }[]; }

const SellerDetails: React.FC = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const seller = state?.seller;

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  
  // NOTE: Removed Modal State (selectedProduct, etc)

  useEffect(() => {
    if (!seller) navigate('/frog-side/users');
  }, [seller, navigate]);

  useEffect(() => {
    if (seller?.id) {
      fetch(`/springboot/api/admin/seller-products/${seller.id}`)
        .then(res => res.json())
        .then(data => setProducts(data))
        .catch(err => console.error(err))
        .finally(() => setLoading(false));
    }
  }, [seller]);

  const deleteProduct = async (e: React.MouseEvent, productId: number) => {
    e.stopPropagation(); 
    if(!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      const res = await fetch(`/springboot/api/admin/delete-product/${productId}`, { method: 'DELETE' });
      if(res.ok) {
        setProducts(prev => prev.filter(p => p.id !== productId));
      }
    } catch (error) { console.error(error); }
  };

  // NEW FUNCTION: Navigate to Product Page
  const handleProductClick = (prod: Product) => {
    navigate('/frog-side/admin-product-details', { 
       state: { product: prod, seller: seller } 
    });
  };

  if (!seller) return null;

  return (
    <div className="space-y-6 pb-20 animate-fade-in-up font-sans">
      
      {/* 1. Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="w-9 h-9 bg-white rounded-full shadow-sm border border-gray-200 flex items-center justify-center text-gray-500 hover:text-[#006d5b] hover:border-[#006d5b] transition-all">
          <FaArrowLeft size={12} />
        </button>
        <h2 className="text-xl font-black text-gray-900 tracking-tight">Seller Profile</h2>
      </div>

      {/* 2. Compact Profile Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col md:flex-row items-center md:justify-between gap-6">
         <div className="flex items-center gap-4 w-full md:w-auto">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#006d5b] to-[#004d40] text-white flex items-center justify-center text-2xl font-black shadow-lg shadow-green-900/20">
               {seller.companyName?.charAt(0) || "S"}
            </div>
            <div>
               <div className="flex items-center gap-2">
                  <h3 className="text-lg font-black text-gray-900">{seller.companyName}</h3>
                  {seller.isVerified && <FaCheckCircle className="text-[#006d5b]" size={14} title="Verified"/>}
               </div>
               <p className="text-xs text-gray-500 font-bold uppercase tracking-wide mt-0.5">Prop: {seller.username}</p>
            </div>
         </div>

         <div className="flex flex-wrap gap-3 w-full md:w-auto md:justify-end">
            <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg border border-gray-100">
               <div className="p-1.5 bg-white rounded-md shadow-sm text-blue-500"><FaEnvelope size={10} /></div>
               <div>
                  <p className="text-[9px] font-black text-gray-400 uppercase">Email</p>
                  <p className="text-xs font-bold text-gray-800">{seller.email}</p>
               </div>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg border border-gray-100">
               <div className="p-1.5 bg-white rounded-md shadow-sm text-green-500"><FaPhone size={10} /></div>
               <div>
                  <p className="text-[9px] font-black text-gray-400 uppercase">Mobile</p>
                  <p className="text-xs font-bold text-gray-800">{seller.mobile || "N/A"}</p>
               </div>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg border border-gray-100">
               <div className="p-1.5 bg-white rounded-md shadow-sm text-purple-500"><FaBoxOpen size={10} /></div>
               <div>
                  <p className="text-[9px] font-black text-gray-400 uppercase">Products</p>
                  <p className="text-xs font-bold text-gray-800">{products.length} Items</p>
               </div>
            </div>
         </div>
      </div>

      {/* 3. Products Grid */}
      <div>
         <div className="flex items-center gap-4 mb-5">
            <h3 className="text-sm font-black text-gray-800 uppercase tracking-widest flex items-center gap-2">Inventory List</h3>
            <div className="h-[1px] flex-1 bg-gray-100"></div>
         </div>

         {loading ? (
            <div className="h-40 flex items-center justify-center">
               <div className="w-8 h-8 border-2 border-[#006d5b] border-t-transparent rounded-full animate-spin"></div>
            </div>
         ) : products.length === 0 ? (
            <div className="bg-gray-50 rounded-xl p-8 text-center border border-dashed border-gray-200">
               <FaBoxOpen size={30} className="text-gray-300 mx-auto mb-3" />
               <p className="text-gray-400 font-bold text-xs">No products found.</p>
            </div>
         ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
               {products.map((prod) => (
                  <div 
                    key={prod.id} 
                    onClick={() => handleProductClick(prod)} // Changed to Navigate
                    className="bg-white rounded-xl border border-gray-100 overflow-hidden cursor-pointer hover:shadow-lg hover:border-[#006d5b]/30 transition-all duration-300 group"
                  >
                     <div className="h-40 bg-gray-50 relative overflow-hidden flex items-center justify-center p-4">
                        {prod.images?.[0] ? (
                           <img src={prod.images[0].imageUrl} alt="prod" className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-500" />
                        ) : (
                           <div className="text-gray-300"><FaBoxOpen size={24} /></div>
                        )}
                        <div className="absolute top-2 left-2">
                           <span className={`text-[9px] font-black px-2 py-0.5 rounded-md uppercase tracking-wide ${prod.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                              {prod.status || "Active"}
                           </span>
                        </div>
                     </div>
                     <div className="p-3">
                        <p className="text-[10px] text-gray-400 font-bold uppercase mb-1 truncate">{prod.category?.name || "General"}</p>
                        <h4 className="font-bold text-gray-900 text-xs leading-tight mb-2 line-clamp-2 h-8">{prod.productName}</h4>
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-50">
                           <span className="font-black text-gray-800 text-sm">₹ {prod.price.toLocaleString()}</span>
                           <button 
                             onClick={(e) => deleteProduct(e, prod.id)}
                             className="text-gray-300 hover:text-red-500 p-1.5 rounded-md hover:bg-red-50 transition-all"
                           >
                             <FaTrash size={10} />
                           </button>
                        </div>
                     </div>
                  </div>
               ))}
            </div>
         )}
      </div>

      {/* NOTE: Modal code is completely removed */}
    </div>
  );
};

export default SellerDetails;